import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { Button } from "@akuna/ui";
import { buildDre, yearTotal, type MonthValues } from "@/lib/financial/calc";
import { DRE_GROUPS, type DreGroup, type FinancialAccount, type FinancialCategory, type FinancialEntry } from "@/lib/financial/types";
import { cn } from "@/lib/utils";
import { DrillDialog, type DrillTarget } from "./DrillDialog";
import { Panel, PillFilter } from "./ui";
import { useFinancialFormat } from "./useFinancialFormat";

interface Props {
  entries: FinancialEntry[];
  categories: FinancialCategory[];
  accounts: FinancialAccount[];
  today: string;
  canEdit: boolean;
}

type RowKind = "group" | "item" | "total" | "big" | "margin";

interface DreRow {
  key: string;
  kind: RowKind;
  label: string;
  hint?: string;
  values: MonthValues;
  yearValue: string;
  vv: string;
  onToggle?: () => void;
  open?: boolean;
  onCell?: (month: number) => void;
}

export function DreTab({ entries, categories, accounts, today, canEdit }: Props) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const currentYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(currentYear);
  const [open, setOpen] = useState<Partial<Record<DreGroup, boolean>>>({});
  const [drill, setDrill] = useState<DrillTarget | null>(null);

  const dre = useMemo(() => buildDre(entries, categories, year, today), [entries, categories, year, today]);
  const rolYear = yearTotal(dre.rol);
  const vv = (v: number) => (rolYear ? fmt.pct(v / rolYear) : "—");
  const allOpen = DRE_GROUPS.every((g) => open[g]);

  const rows: DreRow[] = [];
  const group = (g: DreGroup) => {
    const values = dre.groups[g];
    const total = yearTotal(values);
    rows.push({
      key: g, kind: "group", label: t(`dre.groups.${g}`), hint: t(`dre.groupHints.${g}`), values,
      yearValue: fmt.num(total), vv: vv(total), open: !!open[g],
      onToggle: () => setOpen((p) => ({ ...p, [g]: !p[g] })),
    });
    if (!open[g]) return;
    for (const c of categories.filter((x) => x.dre_group === g).sort((a, b) => a.sort_order - b.sort_order)) {
      const cv = dre.categories.get(c.id) ?? [];
      const ct = yearTotal(cv);
      rows.push({
        key: c.id, kind: "item", label: fmt.category(c), values: cv, yearValue: fmt.num(ct), vv: vv(ct),
        onCell: (m) => setDrill({ category: c, year, month: m }),
      });
    }
  };
  const total = (key: string, label: string, hint: string, values: MonthValues, big = false) => {
    const tv = yearTotal(values);
    rows.push({ key, kind: big ? "big" : "total", label, hint, values, yearValue: fmt.num(tv), vv: vv(tv) });
  };
  const margin = (key: string, label: string, values: MonthValues) => {
    rows.push({
      key, kind: "margin", label,
      values: values.map((v, m) => (v === null || !dre.rol[m] ? null : v / (dre.rol[m] as number))),
      yearValue: rolYear ? fmt.pct(yearTotal(values) / rolYear) : "—", vv: "",
    });
  };

  group("rob");
  group("ded");
  total("rol", t("dre.rol"), t("dre.rolHint"), dre.rol);
  group("cat");
  group("pes");
  group("adm");
  total("ebitda", t("dre.ebitda"), t("dre.ebitdaHint"), dre.ebitda);
  margin("ebitdaMargin", t("dre.ebitdaMargin"), dre.ebitda);
  group("fin");
  total("net", t("dre.net"), t("dre.netHint"), dre.net, true);
  margin("netMargin", t("dre.netMargin"), dre.net);
  group("luc");
  total("after", t("dre.after"), t("dre.afterHint"), dre.afterWithdrawals);

  const robYear = yearTotal(dre.groups.rob);
  const netYear = yearTotal(dre.net);
  const afterYear = yearTotal(dre.afterWithdrawals);
  const per100 = robYear ? (netYear / robYear) * 100 : 0;
  const lastActive = Math.max(0, dre.activeMonths - 1);

  const kpis = [
    { label: t("dre.revenueYear"), value: fmt.brl0(robYear), sub: year === currentYear ? t("dre.monthsRange", { from: fmt.monthShort(0), to: fmt.monthShort(lastActive) }) : t("dre.twelveMonths"), warn: false },
    { label: t("dre.profitYear"), value: fmt.brl0(netYear), sub: t("dre.marginOf", { value: rolYear ? fmt.pct(netYear / rolYear) : "—" }), warn: netYear < 0 },
    { label: t("dre.withdrawals"), value: fmt.brl0(Math.abs(yearTotal(dre.groups.luc))), sub: t("dre.alreadyDeducted"), warn: false },
  ];

  const cellText = (row: DreRow, v: number | null) => (v === null ? "—" : row.kind === "margin" ? fmt.pct(v) : fmt.num(v));
  const cellTone = (row: DreRow, v: number | null) => {
    if (v === null) return "text-muted-foreground/40";
    if (row.kind === "margin") return v < 0 ? "text-state-danger-foreground" : "text-muted-foreground";
    if (row.kind === "group") return v < 0 ? "text-foreground/80" : "text-state-success-foreground";
    if (row.kind === "item") return v < 0 ? "text-foreground/80" : "text-state-success-foreground";
    return v < 0 ? "text-state-danger-foreground" : "text-foreground";
  };

  const months = Array.from({ length: 12 }, (_, m) => m);
  const yearOptions = [currentYear, currentYear - 1].map((y) => ({ value: String(y), label: String(y) }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-5 rounded-2xl bg-foreground p-6 text-background lg:flex-row lg:items-center">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[11px] font-semibold uppercase tracking-[0.14em] opacity-70">{t("dre.howToRead")}</div>
          {robYear ? (
            <>
              <p className="mt-2.5 font-display text-xl font-bold leading-snug tracking-tight sm:text-2xl">{t("dre.sentence1", { value: fmt.moneyInput(Math.abs(per100)) })}</p>
              <p className="mt-2 text-sm opacity-80">{t("dre.sentence2", { rest: fmt.moneyInput(100 - Math.abs(per100)), left: fmt.brl0(afterYear) })}</p>
            </>
          ) : (
            <>
              <p className="mt-2.5 font-display text-xl font-bold leading-snug tracking-tight">{t("dre.noRevenue")}</p>
              <p className="mt-2 text-sm opacity-80">{t("dre.noRevenueSub")}</p>
            </>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:shrink-0">
          {kpis.map((k) => (
            <div key={k.label} className="min-w-[150px] rounded-xl bg-background/10 px-4 py-3.5">
              <div className="text-xs opacity-70">{k.label}</div>
              <div className={cn("mt-1.5 font-display text-xl font-extrabold tracking-tight", k.warn && "text-state-warning")}>{k.value}</div>
              <div className="mt-1 text-xs opacity-70">{k.sub}</div>
            </div>
          ))}
        </div>
      </div>

      <Panel className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-border px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-display text-lg font-bold">{t("dre.resultOf", { year })}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{t("dre.tableHint")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(allOpen ? {} : Object.fromEntries(DRE_GROUPS.map((g) => [g, true])))}>
              {allOpen ? t("dre.closeAll") : t("dre.openAll")}
            </Button>
            <PillFilter options={yearOptions} value={String(year)} onChange={(v) => setYear(Number(v))} label={t("dre.year")} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1240px] text-[13px]">
            <thead>
              <tr className="bg-muted/40">
                <th className="sticky left-0 z-20 w-72 bg-muted px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("dre.description")}</th>
                {months.map((m) => (
                  <th
                    key={m}
                    className={cn(
                      "w-[70px] whitespace-nowrap px-2 py-2.5 text-right text-[11px] font-semibold",
                      m >= dre.activeMonths ? "text-muted-foreground/40" : year === currentYear && m === dre.activeMonths - 1 ? "text-state-warning-foreground" : "text-muted-foreground",
                    )}
                  >
                    {fmt.monthShort(m)}/{String(year).slice(2)}
                  </th>
                ))}
                <th className="w-24 px-3 py-2.5 text-right text-[11px] font-bold uppercase tracking-wider">{t("dre.year")}</th>
                <th className="w-[74px] py-2.5 pl-2 pr-5 text-right text-[11px] font-semibold text-muted-foreground">{t("dre.vv")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const strong = row.kind === "total" || row.kind === "big";
                const bg = row.kind === "big" ? "bg-primary/5" : row.kind === "total" ? "bg-muted/40" : row.kind === "group" && row.open ? "bg-muted/60" : "bg-card";
                // a coluna fixa precisa de fundo opaco para o que rola por baixo não vazar
                const stickyBg = strong || (row.kind === "group" && row.open) ? "bg-muted" : "bg-card";
                const pad = row.kind === "item" || row.kind === "margin" ? "py-2" : "py-3";
                return (
                  <tr key={row.key} className={cn("border-t", strong ? "border-border" : "border-border/50", bg)}>
                    <td className={cn("sticky left-0 z-10 border-r border-border/40 px-5", pad, stickyBg)}>
                      {row.onToggle ? (
                        <button
                          type="button"
                          onClick={row.onToggle}
                          aria-expanded={row.open}
                          className="flex w-full items-start gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <ChevronRight className={cn("mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none", row.open && "rotate-90")} aria-hidden />
                          <span>
                            <span className="block font-display text-[13.5px] font-semibold">{row.label}</span>
                            {row.hint ? <span className="block text-[11.5px] text-muted-foreground">{row.hint}</span> : null}
                          </span>
                        </button>
                      ) : (
                        <div className={cn(row.kind === "item" || row.kind === "margin" ? "pl-5" : "")}>
                          <span className={cn("block", strong ? "font-display font-bold" : row.kind === "margin" ? "text-xs font-medium text-muted-foreground" : "text-[12.5px] text-foreground/80", row.kind === "big" && "text-sm")}>{row.label}</span>
                          {row.hint && strong ? <span className="block text-[11.5px] text-muted-foreground">{row.hint}</span> : null}
                        </div>
                      )}
                    </td>
                    {months.map((m) => {
                      const v = row.values[m] ?? null;
                      const clickable = row.onCell && v !== null;
                      return (
                        <td key={m} className={cn("px-2 text-right tabular-nums", pad, strong && "font-bold", row.kind === "group" && "font-semibold", cellTone(row, v))}>
                          {clickable ? (
                            <button
                              type="button"
                              onClick={() => row.onCell!(m)}
                              aria-label={`${row.label} · ${fmt.monthLong(m)}`}
                              className="w-full rounded px-1 text-right hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              {cellText(row, v)}
                            </button>
                          ) : (
                            cellText(row, v)
                          )}
                        </td>
                      );
                    })}
                    <td className={cn("bg-foreground/[0.03] px-3 text-right font-bold tabular-nums", pad, row.kind === "margin" ? "text-muted-foreground" : row.yearValue.startsWith("−") ? "text-state-danger-foreground" : "text-foreground")}>
                      {row.yearValue}
                    </td>
                    <td className={cn("pl-2 pr-5 text-right text-xs tabular-nums text-muted-foreground", pad)}>{row.vv}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="border-t border-border bg-muted/40 px-5 py-4 text-xs text-muted-foreground">
          {year === currentYear ? t("dre.footerCurrent", { month: fmt.monthLong(lastActive) }) : t("dre.footerClosed")}
        </p>
      </Panel>

      <DrillDialog target={drill} entries={entries} accounts={accounts} today={today} canEdit={canEdit} onClose={() => setDrill(null)} />
    </div>
  );
}
